/** CP-09/CP-10 + OWNEXP Slice 11 — DIE semantische Farbkarte der Control Plane.
 *
 *  Bewusst EINE Karte (DC-71/72): Cockpit-Übersicht, Queue-Tabelle/Kanban,
 *  Missionsdetail, Phasen-Streifen, Admission-Notice, Operator-Controls und
 *  Palette zeigen denselben Zustand nie in verschiedenen Farben. Rohe
 *  Paletten-Farben sind NUR hier erlaubt (Design-Lint erzwingt das); alle
 *  Flächen referenzieren diese Helfer. TK-30..34 aus dem DNA-Bundle:
 *  waiting ≠ warning, unavailable/unknown gedämpft, Severity nur mit Wert. */

const TONE_CLASSES: Record<string, string> = {
  neutral: "bg-jarvis-border/30 text-jarvis-muted border-jarvis-border",
  info: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  success: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  ready: "bg-teal-500/20 text-teal-300 border-teal-500/40",
  waiting: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  warning: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  danger: "bg-red-500/20 text-red-300 border-red-500/40",
  critical: "bg-rose-600/25 text-rose-200 border-rose-600/50",
  blocked: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  unavailable: "bg-jarvis-border/20 text-jarvis-muted border-jarvis-border/60",
  unknown: "bg-jarvis-border/20 text-jarvis-muted border-jarvis-border/60",
};

/** Border-Betonung für Panels/Karten (transparenter Grund, Ton in Rahmen+Text). */
const TONE_BORDER_CLASSES: Record<string, string> = {
  neutral: "border-jarvis-border bg-transparent text-jarvis-muted",
  info: "border-sky-500/40 bg-transparent text-sky-300",
  success: "border-emerald-500/40 bg-transparent text-emerald-300",
  ready: "border-teal-500/40 bg-transparent text-teal-300",
  waiting: "border-orange-500/40 bg-transparent text-orange-300",
  warning: "border-amber-500/40 bg-transparent text-amber-300",
  danger: "border-red-500/40 bg-transparent text-red-300",
  critical: "border-rose-600/50 bg-transparent text-rose-200",
  blocked: "border-zinc-500/40 bg-transparent text-zinc-300",
  unavailable: "border-jarvis-border/50 bg-transparent text-jarvis-muted",
  unknown: "border-jarvis-border/50 bg-transparent text-jarvis-muted",
};

const QUEUE_STATE_TONES: Record<string, string> = {
  READY: "ready",
  RUNNING: "info",
  READY_TO_RESUME: "ready",
  WAIT_RESOURCE: "waiting",
  WAIT_DEPENDENCY: "waiting",
  WAIT_EXTERNAL: "waiting",
  WAIT_HUMAN: "waiting",
  WAIT_OWNER: "waiting",
  VERIFYING: "info",
  BLOCKED: "blocked",
  PARKED: "neutral",
  FAILED: "danger",
  COMPLETED: "success",
  UNAVAILABLE: "unavailable",
  UNKNOWN: "unknown",
};

export function queueStateClass(state: string): string {
  return toneClass(QUEUE_STATE_TONES[state] ?? "unknown");
}

/** Semantischer Ton → Chip-Klassen (eine Farbwelt, ein Ort). */
export function toneClass(tone: string): string {
  return TONE_CLASSES[tone] ?? TONE_CLASSES.unknown ?? "";
}

/** Semantischer Ton → Rahmen-Klassen für Panels/Karten. */
export function toneBorderClass(tone: string): string {
  return TONE_BORDER_CLASSES[tone] ?? TONE_BORDER_CLASSES.unknown ?? "";
}

/** Nur-Text-Variante für inline Hervorhebungen (kein Chip, kein Rahmen). */
const TONE_TEXT_CLASSES: Record<string, string> = {
  neutral: "text-jarvis-muted",
  info: "text-sky-300",
  success: "text-emerald-300",
  ready: "text-teal-300",
  waiting: "text-orange-300",
  warning: "text-amber-300",
  danger: "text-red-300",
  critical: "text-rose-200",
  blocked: "text-zinc-300",
  unavailable: "text-jarvis-muted",
  unknown: "text-jarvis-muted",
};

/** Semantischer Ton → Nur-Text-Klasse. */
export function toneTextClass(tone: string): string {
  return TONE_TEXT_CLASSES[tone] ?? TONE_TEXT_CLASSES.unknown ?? "";
}

/** Prioritätssemantik (Queue-Spalte + Kanban): Bedeutung über Ton, nie Deko. */
export function prioClass(prio: string): string {
  switch (prio) {
    case "urgent":
      return "text-rose-300";
    case "high":
      return "text-amber-300";
    case "low":
      return "text-jarvis-muted";
    default:
      return "text-jarvis-text";
  }
}

/** Missions-Phasen-Streifen (OWNEXP Slice 6): Zustand → Ton. */
export function phasenZustandKlasse(zustand: string): string {
  switch (zustand) {
    case "erreicht":
      return toneBorderClass("success");
    case "aktiv":
      return toneBorderClass("info");
    case "kein_signal":
      return "border-jarvis-border/50 bg-transparent text-jarvis-muted";
    default:
      return toneBorderClass("neutral");
  }
}
